import { useState, useEffect } from 'react';
import { getSponsors, updateSponsors, SponsorTier, Sponsor, SponsorDisplay } from '../api/client';
import ImageUploader from '../components/ImageUploader';
import { normalizeUrlInput } from '../utils/url';
import './Sponsors.css';

function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 6)}`;
}

function Sponsors() {
  const [tiers, setTiers] = useState<SponsorTier[]>([]);
  const [display, setDisplay] = useState<SponsorDisplay>('auto');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadSponsors();
  }, []);

  const loadSponsors = async () => {
    try {
      setIsLoading(true);
      const data = await getSponsors();
      setTiers(data.tiers);
      setDisplay(data.display);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sponsors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setError('');
    setSuccessMessage('');
    setIsSaving(true);
    try {
      await updateSponsors(tiers, display);
      setSuccessMessage('Sponsors saved successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save sponsors');
    } finally {
      setIsSaving(false);
    }
  };

  const addTier = () => {
    if (tiers.length >= 3) return;
    setTiers([...tiers, {
      id: generateId(),
      name: '',
      sponsors: [],
    }]);
  };

  const removeTier = (tierId: string) => {
    setTiers(tiers.filter(t => t.id !== tierId));
  };

  const updateTierName = (tierId: string, name: string) => {
    setTiers(tiers.map(t => t.id === tierId ? { ...t, name } : t));
  };

  const addSponsor = (tierId: string) => {
    setTiers(tiers.map(t => {
      if (t.id !== tierId) return t;
      return {
        ...t,
        sponsors: [...t.sponsors, { id: generateId(), name: '', image: '', url: '' }],
      };
    }));
  };

  const removeSponsor = (tierId: string, sponsorId: string) => {
    setTiers(tiers.map(t => {
      if (t.id !== tierId) return t;
      return {
        ...t,
        sponsors: t.sponsors.filter(s => s.id !== sponsorId),
      };
    }));
  };

  const updateSponsor = (tierId: string, sponsorId: string, field: keyof Sponsor, value: string) => {
    setTiers(tiers.map(t => {
      if (t.id !== tierId) return t;
      return {
        ...t,
        sponsors: t.sponsors.map(s =>
          s.id === sponsorId ? { ...s, [field]: value } : s
        ),
      };
    }));
  };

  if (isLoading) {
    return <div className="loading">Loading sponsors...</div>;
  }

  return (
    <div className="sponsors-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Sponsors</h1>
          <p className="page-subtitle">Manage your club's sponsors and partners</p>
        </div>
        <div className="page-header-actions">
          {tiers.length < 3 && (
            <button onClick={addTier} className="btn btn-secondary">
              Add Tier
            </button>
          )}
          <button onClick={handleSave} className="btn btn-primary" disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      {/* Layout choice. 'auto' is exactly what every club had before this
          setting existed, so an untouched club is unaffected. */}
      <div className="sponsor-display-choice">
        <label htmlFor="sponsorDisplay">Layout on your website</label>
        <select id="sponsorDisplay" value={display}
                onChange={(e) => setDisplay(e.target.value as SponsorDisplay)}>
          <option value="auto">Automatic, rows, scrolling once you have 7 or more</option>
          <option value="paged">One row with arrows, each sponsor shown once</option>
        </select>
        <p className="form-hint">
          The scrolling layout repeats your logos so the strip never shows a gap.
          At around seven sponsors that repeat becomes visible on screen, pick the
          arrows instead and each logo appears exactly once.
        </p>
      </div>

      {tiers.length === 0 && (
        <div className="empty-state">
          <h3>No sponsor tiers yet</h3>
          <p>Create your first tier to start adding sponsors. You can have up to 3 tiers (e.g. Gold, Silver, Bronze).</p>
          <button onClick={addTier} className="btn btn-primary">
            Create First Tier
          </button>
        </div>
      )}

      <div className="tiers-list">
        {tiers.map((tier, tierIndex) => (
          <div key={tier.id} className="tier-card">
            <div className="tier-header">
              <div className="tier-header-left">
                <span className="tier-number">Tier {tierIndex + 1}</span>
                <input
                  type="text"
                  className="tier-name-input"
                  placeholder="Tier name (e.g. Gold Sponsors)"
                  value={tier.name}
                  onChange={(e) => updateTierName(tier.id, e.target.value)}
                />
              </div>
              <button
                onClick={() => removeTier(tier.id)}
                className="btn btn-danger btn-small"
              >
                Remove Tier
              </button>
            </div>

            <div className="sponsors-grid">
              {tier.sponsors.map((sponsor) => (
                <div key={sponsor.id} className="sponsor-card">
                  <div className="sponsor-image-area">
                    {sponsor.image ? (
                      <div className="sponsor-image-preview">
                        <img src={sponsor.image} alt={sponsor.name} />
                        <button
                          type="button"
                          className="sponsor-image-remove"
                          onClick={() => updateSponsor(tier.id, sponsor.id, 'image', '')}
                        >
                          &times;
                        </button>
                      </div>
                    ) : (
                      <ImageUploader
                        onUpload={(url) => updateSponsor(tier.id, sponsor.id, 'image', url)}
                        label="Upload Logo"
                      />
                    )}
                  </div>
                  <div className="sponsor-fields">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Sponsor name"
                      value={sponsor.name}
                      onChange={(e) => updateSponsor(tier.id, sponsor.id, 'name', e.target.value)}
                    />
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Website (optional), e.g. www.sponsor.co.uk"
                      value={sponsor.url || ''}
                      onChange={(e) => updateSponsor(tier.id, sponsor.id, 'url', e.target.value)}
                      onBlur={(e) => updateSponsor(tier.id, sponsor.id, 'url', normalizeUrlInput(e.target.value))}
                    />
                  </div>
                  <button
                    onClick={() => removeSponsor(tier.id, sponsor.id)}
                    className="sponsor-remove-btn"
                    title="Remove sponsor"
                  >
                    &times;
                  </button>
                </div>
              ))}

              <button
                onClick={() => addSponsor(tier.id)}
                className="add-sponsor-btn"
              >
                <span className="add-sponsor-icon">+</span>
                <span>Add Sponsor</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Sponsors;
